import { useEffect, useRef } from 'react';

/**
 * Drop-in replacement for `useEffect(() => { fn(); setInterval(fn, ms); ... })`
 * that respects `document.visibilityState`.
 *
 * - Tab visible: polls at `intervalMs`.
 * - Tab hidden:   polls at `hiddenIntervalMs` (default 60s) — keeps state
 *                 from going totally stale, but stops the 5s federation
 *                 fan-out when nobody's looking.
 * - Tab returns:  fires immediately so the user sees fresh data on
 *                 focus, then resumes the visible cadence.
 *
 * Five tabs polling /sessions/all every 5s = 25 federation calls per 5s
 * across mac+arkserver+arkturian+oneal+pdrei. With this hook, hidden
 * tabs drop to one call per minute and the active tab keeps full speed.
 */
export function useVisiblePolling(
  fn: () => void | Promise<void>,
  intervalMs: number,
  hiddenIntervalMs: number = 60000,
) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    // intervalMs <= 0 → polling disabled entirely (no initial fire, no
    // timer, no visibility handler). Lets a caller opt out of the safety
    // poll and rely purely on SSE + return-catch-up (Alex 2026-06-19).
    if (intervalMs <= 0) return;

    let timer: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (timer) clearInterval(timer);
      const period = document.hidden ? hiddenIntervalMs : intervalMs;
      timer = setInterval(() => fnRef.current(), period);
    };

    const onVisibilityChange = () => {
      if (!document.hidden) {
        // Returned to foreground: refresh immediately, then resume fast cadence.
        fnRef.current();
      }
      start();
    };

    fnRef.current();
    start();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [intervalMs, hiddenIntervalMs]);
}
